/**
 * `get-history` / `entries action="history"` handler body (WP-484 S1: extracted so both callers
 * share it). Split out of tools/knowledge.ts (not inline) — STD-2/DEC-1504: knowledge.ts is a
 * grandfathered, shrink-only file; this extraction is the WP-582 B1 offset for that PR's
 * publishAdvisories render addition.
 */
import { z } from "zod/v3";
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { kernelQuery } from "../../client.js";
import { success, successResult, type ToolResult } from "../../envelope.js";

export const getHistorySchema = z.object({
  entryId: z.string().describe("Entry ID, e.g. 'T-SUPPLIER', '<PREFIX>-<n>'"),
});

export async function handleGetHistory({ entryId }: z.infer<typeof getHistorySchema>): Promise<ToolResult> {
      const history = await kernelQuery<GatewayRouteReturnByName<"chain.listEntryHistory">>("chain.listEntryHistory", { entryId });

      if (history.length === 0) {
        return successResult(
          `No history found for ${entryId}.`,
          `No history events recorded for ${entryId}.`,
          { entryId, eventCount: 0, events: [] },
        );
      }

      const formatted = history
        .map((rawH) => {
          // `annotateVerifiedHistory`'s generic (convex/lib/attestEntry.ts, shared by both
          // connectors) infers its type parameter as narrowly as its own constraint
          // (Pick<Doc<'entryHistory'>, 'event'> & Partial<...>) rather than the full raw
          // doc it's actually called with — `timestamp`/`changes`/`changedBy` are real
          // fields on the row but not on the inferred element type (PR #395, Lane C S3b
          // annotation pass). Cast here rather than widen the shared generic's constraint.
          const h = rawH as typeof rawH & { timestamp: number; changes?: unknown; changedBy?: string };
          const date = new Date(h.timestamp).toISOString();
          const changes = h.changes ? ` — ${JSON.stringify(h.changes)}` : "";
          // WP-D (attestation spec §7.2): server-derived qualifier beside legacy 'verified'
          // events — the ledger sentence stays as written, the display stops lying.
          const qualifier = h.legacyQualifier ? ` [${h.legacyQualifier}]` : "";
          return `- **${date}** ${h.event}${h.changedBy ? ` _(${h.changedBy})_` : ""}${changes}${qualifier}`;
        })
        .join("\n");

      return {
        content: [{ type: "text" as const, text: `# History for \`${entryId}\` (${history.length} events)\n\n${formatted}` }],
        structuredContent: success(
          `Found ${history.length} history events for ${entryId}.`,
          { entryId, eventCount: history.length, events: history },
        ),
      };
}
